import { Group, Text } from "@mantine/core"
import { IconBan } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import {
  Missing,
  BoolCell,
  CountryCell,
  DateCell,
  getCheckboxColumnDefs,
  QRCodeCell,
  TextCell,
} from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { LastPendingIrContactCell } from "~/components/cells/LastPendingIrContactCell.tsx"
import { SupplierCell } from "~/components/cells/SupplierCell.tsx"
import { FindAgecComplianceResponseDto } from "~/gen/api"
import { AgecComplianceStepCell } from "~/pages/Brand/Regulations/AGEC/AgecComplianceStepCell.tsx"
import { MaterialPercentage } from "~/pages/Brand/Regulations/AGEC/MaterialPercentage.tsx"
import { COUNTRIES } from "~/utils/countries"

const columnHelper = createColumnHelper<FindAgecComplianceResponseDto>()

export const useAgecColumns = (
  hasAgecComplianceFeatureAccess: boolean
): ColumnDef<FindAgecComplianceResponseDto, any>[] => {
  const { t } = useTranslation("products")

  return useMemo<ColumnDef<FindAgecComplianceResponseDto, any>[]>(() => {
    const agecColumn = hasAgecComplianceFeatureAccess
      ? columnHelper.accessor("agecComplianceStep", {
          id: "agecCompliance",
          header: t("lists.columns.agecCompliance"),
          enableSorting: false,
          cell: (info) => <AgecComplianceStepCell step={info.getValue()} />,
        })
      : columnHelper.accessor("agecCompliance", {
          id: "agecCompliance",
          header: t("lists.columns.agecCompliance"),
          enableSorting: false,
          cell: (info) => {
            const value = info.getValue()
            if (value === null || value === undefined) {
              return (
                <Group gap={4}>
                  <IconBan size={16} />
                  <Text fz="xs">{t("lists.cells.agec.notApplicable")}</Text>
                </Group>
              )
            }
            return <BoolCell value={value} />
          },
        })

    const metalloplasticColumn = hasAgecComplianceFeatureAccess
      ? [
          columnHelper.accessor("metalloplasticPercentage", {
            header: t("lists.columns.metalloplasticPercentage"),
            enableSorting: false,
            cell: (info) => (
              <MaterialPercentage
                percentage={info.getValue()}
                type="metalloplastic"
              />
            ),
          }),
        ]
      : []

    return [
      getCheckboxColumnDefs<FindAgecComplianceResponseDto>(),
      columnHelper.accessor("name", {
        id: "name",
        header: t("lists.columns.name"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),

      columnHelper.accessor("reference", {
        id: "reference",
        header: t("lists.columns.reference"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("purchaseOrder.reference", {
        id: "purchaseOrder",
        header: t("lists.columns.purchaseOrder"),
        enableSorting: false,
        cell: (info) => (
          <TextCell value={info.row.original.purchaseOrder?.reference} />
        ),
      }),
      columnHelper.accessor("ingestedAt", {
        id: "ingestedAt",
        header: t("lists.columns.ingestedAt"),
        enableSorting: false,
        cell: ({ getValue }) => <DateCell date={getValue()} />,
      }),
      agecColumn,
      columnHelper.accessor(
        ({ agecActivityCountries }) => agecActivityCountries[0],
        {
          header: t("lists.columns.agecCountry1"),
          enableSorting: false,
          id: "agecCountry1",
          cell: ({ getValue }) => {
            return getValue()?.country ? (
              <CountryCell
                fw={700}
                countryIso3166={getValue().country.country}
                countriesMapper={COUNTRIES}
              />
            ) : (
              <Missing />
            )
          },
        }
      ),
      columnHelper.accessor(
        ({ agecActivityCountries }) => agecActivityCountries[1],
        {
          header: t("lists.columns.agecCountry2"),
          enableSorting: false,
          id: "agecCountry2",
          cell: ({ getValue }) => {
            return getValue()?.country ? (
              <CountryCell
                fw={700}
                countryIso3166={getValue().country.country}
                countriesMapper={COUNTRIES}
              />
            ) : (
              <Missing />
            )
          },
        }
      ),
      columnHelper.accessor(
        ({ agecActivityCountries }) => agecActivityCountries[2],
        {
          header: t("lists.columns.agecCountry3"),
          enableSorting: false,
          id: "agecCountry3",
          cell: (info) => {
            return info.getValue()?.country ? (
              <CountryCell
                fw={700}
                countryIso3166={info.getValue().country.country}
                countriesMapper={COUNTRIES}
              />
            ) : (
              <Missing />
            )
          },
        }
      ),
      columnHelper.accessor("recycledMaterial", {
        header: t("lists.columns.recycledMaterialPercentage"),
        enableSorting: false,
        cell: (info) => (
          <MaterialPercentage percentage={info.getValue()} type="recycled" />
        ),
      }),
      ...metalloplasticColumn,
      columnHelper.accessor("microplastics", {
        header: t("lists.columns.releaseMicroplastics"),
        enableSorting: false,
        cell: (info) => {
          const val = info.getValue()
          return val !== "missing-main-component" ? (
            <BoolCell value={val} />
          ) : (
            <BoolCell value={undefined} />
          )
        },
      }),
      columnHelper.accessor("directSupplier", {
        id: "supplier",
        header: t("lists.columns.directSupplier"),
        enableSorting: false,
        cell: (info) => {
          const value = info.row.original
          return <SupplierCell supplierEmail={value.directSupplier.email} />
        },
      }),
      columnHelper.accessor("lastPendingIrContact", {
        header: t("lists.columns.pendingIrContact"),
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue()
          return <LastPendingIrContactCell supplier={value} />
        },
      }),
      columnHelper.accessor("agecShow", {
        id: "agecShow",
        header: t("lists.columns.agecShow"),
        enableSorting: false,
        cell: (info) => (
          <TextCell
            value={
              info.getValue()
                ? t("lists.agecShow.visible")
                : t("lists.agecShow.hidden")
            }
          />
        ),
      }),
      columnHelper.accessor("agecQrCodeUrl", {
        header: undefined,
        id: "action-cell-qrCode",
        enableSorting: false,
        cell: (info) => (
          <QRCodeCell value={info.row.original.agecPopinUrl ?? undefined} />
        ),
      }),
    ]
  }, [hasAgecComplianceFeatureAccess, t])
}
